import _ from 'lodash'
import {
	pointers,
	ReactionDataItem,
	UnresolvedReactions,
	UnresolvedRefArray,
	UnresolvedVariantRelation,
} from '@wix/thunderbolt-becky-types'
import {
	PRESET_RESOLVING_DATA_ITEM_TYPE,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	REACTIONS_INFLATED_MAP,
	STRUCTURE_ASPECT,
} from '../../../constants'
import { resolveOverrideItem } from '../../dataConsumption'
import { getInflatedDataItemsMap } from '../../presetsResolving'
import type { DataRefsForPresetResolving } from '../../types'
import type { DependenciesData } from './types'
import { createConsumptionDataNode, getDataRefsForPresetResolving } from './utils/dataNodeUtils'
import { InflationDataItem } from '@wix/thunderbolt-becky-root'
import { getMultipleRefCompsIdFromInflatedId, getUniqueId } from '@wix/thunderbolt-commons'
import { removeHash } from '@wix/thunderbolt-catharsis'
import { getRefCompIds } from '../../../byRefUtils'

export const Reactions = createConsumptionDataNode<
	DependenciesData,
	DataRefsForPresetResolving,
	DataRefsForPresetResolving
>({
	getDependencies: getDataRefsForPresetResolving,
	updateDependencies: ({ update, inflatedComponent, dependencies }) => {
		update(
			RESOLVED_REFS_NAMESPACE,
			REACTIONS_INFLATED_MAP,
			inflatedComponent.reactionsQuery,
			dependencies,
			dependencies
		)
	},
	toViewItem: ({ id, value, rawModel }) => {
		const { refArraysWithOverrides } = value
		const templateDataItem = _.last(refArraysWithOverrides)
		if (!templateDataItem) {
			return
		}

		if (templateDataItem.type !== 'RefArray') {
			return { [id]: resolveOverrideItem(refArraysWithOverrides, id) }
		}

		const pageId = _.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT, getRefCompIds(id)[0], 'metaData', 'pageId'])
		// when in blocks we need to separate the variants per page, but in public we don't have the relevant data
		const pageVariantsSpecific = _.get(rawModel, ['variants_per_page', pageId], {})
		const masterPageVariants = _.get(rawModel, ['variants_per_page', 'masterPage'], {})
		const pageVariants = _.isEmpty(pageVariantsSpecific)
			? _.get(rawModel, [SITE_NAMESPACE, pointers.data.DATA_MAPS.VARIANTS], {})
			: _.merge(pageVariantsSpecific, masterPageVariants)
		const presetResolvedData = getInflatedDataItemsMap(
			value,
			id,
			pageVariants,
			PRESET_RESOLVING_DATA_ITEM_TYPE[pointers.data.DATA_MAPS.REACTIONS]
		)
		const unresolvedInnerReactions: Record<
			string,
			UnresolvedRefArray | UnresolvedVariantRelation | InflationDataItem | UnresolvedReactions | ReactionDataItem
		> = {}

		const ancestorsChain = getMultipleRefCompsIdFromInflatedId(id)
		_.forEach(presetResolvedData, (dataItem, dataItemId) => {
			if (dataItem.type === 'Reactions') {
				const values = dataItem.values.map((v) => {
					const inflatedId = `${id}-${removeHash(v.id)}`

					const inflatedState =
						'state' in v ? `#${getUniqueId(ancestorsChain, removeHash(v.state))}` : undefined

					const inflatedEffect =
						'effect' in v ? `#${getUniqueId(ancestorsChain, removeHash(v.effect))}` : undefined

					unresolvedInnerReactions[removeHash(inflatedId)] = {
						...v,
						...(inflatedState && { state: inflatedState }),
						...(inflatedEffect && { effect: inflatedEffect }),
						id: inflatedId,
					}

					return `#${inflatedId}`
				})

				unresolvedInnerReactions[dataItemId] = { ...dataItem, values }
			} else {
				unresolvedInnerReactions[dataItemId] = dataItem
			}
		})

		return unresolvedInnerReactions
	},
})
